import React from "react"

import type { IconTileVariant } from "../tokens"

type IconComponent = React.FC<React.SVGProps<SVGSVGElement>>

const Svg = ({ children, ...props }: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    {children}
  </svg>
)

const BriefcaseIcon: IconComponent = (props) => (
  <Svg {...props}>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </Svg>
)

const ClockIcon: IconComponent = (props) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Svg>
)

const ClipboardCheckIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M9 11l3 3 8-8" />
    <path d="M21 12v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h11" />
  </Svg>
)

const GaugeIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M21 12a9 9 0 1 1-9-9" />
    <path d="M12 12 20 4" />
  </Svg>
)

const CurrencyIcon: IconComponent = (props) => (
  <Svg {...props}>
    <line x1="12" y1="2" x2="12" y2="22" />
    <path d="M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </Svg>
)

const ReceiptIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M4 4h16v14l-3-2-2 2-3-2-3 2-2-2-3 2z" />
    <path d="M8 9h8M8 13h5" />
  </Svg>
)

const TrendingUpIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M3 17l6-6 4 4 7-7" />
    <path d="M17 8h4v4" />
  </Svg>
)

const TrophyIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0z" />
    <path d="M5 4H3v2a3 3 0 0 0 3 3M19 4h2v2a3 3 0 0 1-3 3" />
  </Svg>
)

const ActivityIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M3 12h4l3 8 4-16 3 8h4" />
  </Svg>
)

const WalletIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M3 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <path d="M16 12h4M16 12a1.5 1.5 0 0 0 0 3h4v-3z" />
  </Svg>
)

const TargetIcon: IconComponent = (props) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1" />
  </Svg>
)

const DocumentIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </Svg>
)

const AlertIcon: IconComponent = (props) => (
  <Svg {...props}>
    <path d="M12 3 2 20h20L12 3z" />
    <path d="M12 10v4M12 17h.01" />
  </Svg>
)

interface KpiIconEntry {
  icon: IconComponent
  tint: IconTileVariant
}

const kpiIconMap: Record<string, KpiIconEntry> = {
  active_projects: { icon: BriefcaseIcon, tint: "teal" },
  delayed_projects: { icon: ClockIcon, tint: "error" },
  pending_approvals: { icon: ClipboardCheckIcon, tint: "teal" },
  budget_health: { icon: GaugeIcon, tint: "warning" },
  revenue_in_window: { icon: CurrencyIcon, tint: "teal" },
  outstanding_receivables: { icon: ReceiptIcon, tint: "teal" },
  gross_margin: { icon: TrendingUpIcon, tint: "teal" },
  win_rate: { icon: TrophyIcon, tint: "teal" },
  global_files_total: { icon: DocumentIcon, tint: "teal" },
  global_files_proposals: { icon: ClipboardCheckIcon, tint: "teal" },
  global_files_contracts: { icon: BriefcaseIcon, tint: "teal" },
  global_files_needs_attention: { icon: AlertIcon, tint: "error" }
}

const iconRegistry: Record<string, IconComponent> = {
  briefcase: BriefcaseIcon,
  clock: ClockIcon,
  check: ClipboardCheckIcon,
  gauge: GaugeIcon,
  currency: CurrencyIcon,
  receipt: ReceiptIcon,
  trending: TrendingUpIcon,
  trophy: TrophyIcon,
  wallet: WalletIcon,
  target: TargetIcon,
  activity: ActivityIcon,
  document: DocumentIcon,
  alert: AlertIcon
}

const defaultEntry: KpiIconEntry = { icon: ActivityIcon, tint: "teal" }

export function getKpiIconEntry(tile: {
  id: string
  icon?: string
}): KpiIconEntry {
  const byId = kpiIconMap[tile.id]
  if (byId) return byId
  const byIcon = tile.icon ? iconRegistry[tile.icon] : undefined
  if (byIcon) return { icon: byIcon, tint: "teal" }
  return defaultEntry
}
